/** Frozen Friendship card vocabulary. No Romantic imports or inferred answers. */
export const FRIENDSHIP_CARD_VERSION = "friendship-card@1.0.0";
export const FRIENDSHIP_ACK_VERSION = "friendship-participation-acknowledgement@1.0.0";
export interface Question {
    id: string;
    label: string;
    kind: "text" | "single" | "multi" | "location" | "tags";
    options: readonly (readonly [
        string,
        string
    ])[];
    limit?: number;
    supplementLimit?: number;
    otherOnly?: boolean;
}
export const QUESTIONS: readonly Question[] = [
    { id: "Q01", label: "想和别人一起做什么？", kind: "text", limit: 240, options: [] },
    { id: "Q02a", label: "想做一次，还是定期做？", kind: "single", options: [["once", "先做一次"], ["regular", "想定期做"], ["undecided", "还没定"]], supplementLimit: 40 },
    { id: "Q02b", label: "哪些时段方便？", kind: "multi", options: [["weekday_morning", "工作日上午"], ["weekday_afternoon", "工作日下午"], ["weekday_evening", "工作日晚上"], ["weekend_morning", "周末上午"], ["weekend_afternoon", "周末下午"], ["weekend_evening", "周末晚上"], ["flexible", "不固定，可商量"]], supplementLimit: 40 },
    { id: "Q03", label: "在哪里方便参加？", kind: "location", options: [] },
    { id: "Q04", label: "这次想做得多轻松？", kind: "single", options: [["easy", "轻松随意"], ["some_challenge", "有一点挑战"], ["challenging", "比较有挑战"], ["undecided", "还没定"]], supplementLimit: 60 },
    { id: "Q05a", label: "这件事有费用吗？", kind: "single", options: [["free", "没有费用"], ["aa", "有，AA"], ["uncertain", "还不确定"]], supplementLimit: 40 },
    { id: "Q05b", label: "大概花在哪？", kind: "multi", options: [["tickets", "门票"], ["venue", "场地"], ["travel", "交通"], ["food", "吃饭"], ["equipment", "装备租借"], ["other", "其他"]], supplementLimit: 40, otherOnly: true },
    { id: "Q05c", label: "每人大概多少？", kind: "single", options: [["under_50", "¥50 以内"], ["50_100", "¥50–100"], ["100_200", "¥100–200"], ["over_200", "¥200 以上"], ["uncertain", "还不确定"]], supplementLimit: 40 },
    { id: "Q06", label: "需要用到什么？", kind: "tags", options: [["own", "各自准备"], ["share", "我有，可以共用"], ["discuss", "到时候商量"]], supplementLimit: 40 },
    { id: "Q07", label: "为了这件事，你可以帮忙做什么？", kind: "multi", options: [["book", "订场地或门票"], ["plan", "查路线或攻略"], ["bring", "带需要的东西"], ["coordinate", "联系大家、定时间"], ["beginners", "带一带新手"], ["other", "其他"]], supplementLimit: 40, otherOnly: true },
    { id: "Q08", label: "如果临时有变，你希望怎样？", kind: "multi", options: [["reschedule", "改个时间再约"], ["cancel", "这次取消也没关系"], ["discuss", "到时候再商量"], ["other", "其他"]], supplementLimit: 40, otherOnly: true },
    { id: "Q09", label: "一起做的时候，希望怎样相处？", kind: "multi", options: [["quiet", "安静地一起做事"], ["chat", "边做边聊"], ["after", "做完一起吃点东西聊聊"], ["casual", "随意"]], supplementLimit: 40 },
    { id: "Q10", label: "一起做这件事，有什么不希望发生的？", kind: "text", limit: 120, options: [] },
    { id: "Q11a", label: "这件事你以前做过吗？", kind: "single", options: [["done", "做过"], ["first", "第一次，想找人一起试试"], ["private", "不想说"]], supplementLimit: 40 },
    { id: "Q11b", label: "上一次做的时候，你具体做了什么？", kind: "text", limit: 240, options: [] },
];
export interface Answer {
    public: boolean;
    selected?: string[];
    text?: string;
    supplement?: string;
    tags?: string[];
    cityId?: string | null;
    wholeCity?: boolean;
    online?: boolean;
}
export interface CardDraft {
    version: typeof FRIENDSHIP_CARD_VERSION;
    answers: Record<string, Answer>;
    opened: string[];
    showFirst?: boolean | null;
}
export interface ProjectionItem {
    id: string;
    label: string;
    values: string[];
    texts: string[];
}
export interface CardProjection {
    version: typeof FRIENDSHIP_CARD_VERSION;
    items: ProjectionItem[];
}
export function emptyCard(): CardDraft { return { version: FRIENDSHIP_CARD_VERSION, answers: {}, opened: [] }; }
const object = (x: unknown): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x);
export function validateDraft(value: unknown): string[] {
    const errors: string[] = [];
    if (!object(value) || value.version !== FRIENDSHIP_CARD_VERSION || !object(value.answers) || !Array.isArray(value.opened))
        return ["卡片格式无效"];
    if (Object.keys(value).some(k => !["version", "answers", "opened", "showFirst"].includes(k)))
        errors.push("未知卡片字段");
    if (value.showFirst !== undefined && value.showFirst !== null && typeof value.showFirst !== "boolean")
        errors.push("第一次披露选择无效");
    if (value.opened.some(x => !["Q04", "Q05a", "Q06"].includes(x)) || new Set(value.opened).size !== value.opened.length)
        errors.push("展开分组无效");
    for (const [id, a] of Object.entries(value.answers)) {
        const q = QUESTIONS.find(q => q.id === id);
        if (!q || !object(a)) {
            errors.push("未知题目");
            continue;
        }
        const allowed = ["public", ...(q.options.length ? ["selected"] : []), ...(q.limit ? ["text"] : []), ...(q.supplementLimit ? ["supplement"] : []), ...(q.kind === "tags" ? ["tags"] : []), ...(q.kind === "location" ? ["cityId", "wholeCity", "online"] : [])];
        if (Object.keys(a).some(k => !allowed.includes(k)) || typeof a.public !== "boolean")
            errors.push(`${id} 字段无效`);
        for (const [key, limit] of [["text", q.limit], ["supplement", q.supplementLimit]] as const) {
            if (a[key] !== undefined && (typeof a[key] !== "string" || !limit || [...a[key] as string].length > limit || /[\u0000]/u.test(a[key] as string)))
                errors.push(`${id} 文字不能超过 ${limit ?? 0} 字`);
        }
        if (a.selected !== undefined && (!Array.isArray(a.selected) || a.selected.some(x => !q.options.some(([id]) => id === x)) || new Set(a.selected).size !== a.selected.length || (q.kind !== "multi" && a.selected.length > 1)))
            errors.push(`${id} 选项无效`);
        if (a.tags !== undefined && (!Array.isArray(a.tags) || a.tags.length > 5 || a.tags.some(x => typeof x !== "string" || [...x].length > 12 || x.includes("\0"))))
            errors.push(`${id} 最多 5 个词，每个不超过 12 字`);
        if (q.kind === "location" && ((a.cityId !== undefined && a.cityId !== null && (typeof a.cityId !== "string" || !a.cityId.trim() || a.cityId.length > 100)) || [a.online, a.wholeCity].some(x => x !== undefined && typeof x !== "boolean") || (a.wholeCity && !a.cityId)))
            errors.push("请选择有效城市");
    }
    return errors;
}
export function questionActive(d: CardDraft, id: string): boolean {
    if (["Q04", "Q06"].includes(id))
        return d.opened.includes(id);
    if (id.startsWith("Q05"))
        return d.opened.includes("Q05a") && (id === "Q05a" || d.answers.Q05a?.selected?.[0] === "aa");
    if (id === "Q11b")
        return d.answers.Q11a?.selected?.[0] === "done";
    if (id === "Q11a")
        return d.answers.Q11a?.selected?.[0] !== "private" && (d.answers.Q11a?.selected?.[0] !== "first" || d.showFirst === true);
    return true;
}
/** A branch transition never silently restores old public choices. Text remains private. */
export function transitionDraft(previous: CardDraft, next: CardDraft): CardDraft {
    const d = structuredClone(next);
    const changed = (id: string) => previous.answers[id]?.selected?.[0] !== next.answers[id]?.selected?.[0];
    if (changed("Q05a"))
        for (const id of ["Q05b", "Q05c"])
            if (d.answers[id])
                d.answers[id].public = false;
    if (changed("Q11a")) {
        if (d.answers.Q11b)
            d.answers.Q11b.public = false;
        d.showFirst = null;
    }
    return d;
}
export function publicDocument(d: CardDraft): CardDraft {
    const out = emptyCard();
    out.opened = [...d.opened];
    out.showFirst = d.showFirst === true ? true : null;
    for (const q of QUESTIONS) {
        const a = d.answers[q.id];
        if (!a?.public || !questionActive(d, q.id))
            continue;
        const b = structuredClone(a);
        if (q.otherOnly && !b.selected?.includes("other"))
            delete b.supplement;
        out.answers[q.id] = b;
    }
    // Parent branch controls may be needed even when their labels are private.
    if (Object.keys(out.answers).some(id => id === "Q05b" || id === "Q05c") && !out.answers.Q05a)
        out.answers.Q05a = { public: false, selected: ["aa"] };
    if (out.answers.Q11b && !out.answers.Q11a)
        out.answers.Q11a = { public: false, selected: ["done"] };
    return out;
}
export function projectCard(d: CardDraft, cityLabel?: string | null): CardProjection {
    const items: ProjectionItem[] = [];
    for (const q of QUESTIONS) {
        const a = d.answers[q.id];
        if (!a?.public || !questionActive(d, q.id))
            continue;
        const values: string[] = [], texts: string[] = [];
        if (q.kind === "location") {
            if (a.cityId && cityLabel)
                values.push(cityLabel);
            if (a.cityId && cityLabel && a.wholeCity)
                values.push("整个城市都可以");
            if (a.online)
                values.push(a.cityId ? "线上也可以" : "线上");
        }
        else {
            for (const [id, label] of q.options)
                if (a.selected?.includes(id) && id !== "other" && !(q.id === "Q11a" && id !== "first"))
                    values.push(label);
            if (a.text?.trim()) {
                values.push(a.text);
                texts.push(a.text);
            }
            if (a.supplement?.trim() && (!q.otherOnly || a.selected?.includes("other"))) {
                values.push(a.supplement);
                texts.push(a.supplement);
            }
            const tags = a.tags?.filter(t => t.trim()) ?? [];
            values.push(...tags);
            texts.push(...tags);
            if (tags.length > 1)
                texts.push(tags.join(" "));
        }
        if (values.length)
            items.push({ id: q.id, label: q.label, values, texts });
    }
    return { version: FRIENDSHIP_CARD_VERSION, items };
}
export function hasPublishableContent(p: CardProjection): boolean { return p.items.some(i => i.values.some(v => v.trim().length > 0)); }
export function copySelected(source: CardDraft, ids: string[]): CardDraft {
    const result = emptyCard();
    result.opened = [...source.opened];
    for (const id of ids)
        if (QUESTIONS.some(q => q.id === id) && source.answers[id])
            result.answers[id] = { ...structuredClone(source.answers[id]), public: false };
    return result;
}
